import { create } from "zustand";
import { axiosInstance } from "../lib/axios";
import toast from "react-hot-toast";

export type TargetMetric = "revenue" | "bids" | "connects" | "proposals";
export type TargetPeriod = "weekly" | "monthly";

export interface BdTarget {
  _id: string;
  businessDeveloper: { _id: string; fullName: string; email: string; role?: string } | string;

  // Added with recurring targets. Optional because rows created before them
  // carry neither, and the server treats a missing value as a monthly revenue
  // target — which is exactly what those rows were.
  metric?: TargetMetric;
  period?: TargetPeriod;
  targetValue?: number;
  // "2026-M10" or "2026-W43" — identifies the cycle this row measures.
  periodKey?: string;
  // Set on the standing instruction. Its per-period rows carry `spawnedFrom`
  // instead, pointing back at it.
  recurring?: boolean;
  spawnedFrom?: string | null;

  targetMonth: number;
  targetYear: number;
  revenueTarget: number;
  effectiveFrom: string;
  effectiveTo: string;
  status: "Active" | "Archived";
  notes: string;
  isClosed?: boolean;
  closedAt?: string | null;
}

// A live target with its progress, from /bd-target-bonus/my-targets.
export interface TargetProgress {
  id: string;
  metric: TargetMetric;
  metricLabel: string;
  unit: "currency" | "count";
  source: string;
  period: TargetPeriod;
  periodKey: string;
  periodLabel: string;
  recurring: boolean;
  target: number;
  achieved: number;
  // Null when there is no target to measure against — never 0%.
  pct: number | null;
  remaining: number;
  requiredToStayOnTrack: number;
  pacePct: number;
  status: "No Target" | "Target Achieved" | "On Track" | "Slightly Behind" | "Behind Target";
  behind: boolean;
}

export interface BdBonusTier {
  _id?: string;
  tierName: string;
  revenueMilestone: number;
  bonusType: "Fixed Amount" | "Percentage";
  bonusValue: number;
  notes?: string;
  status?: "Active" | "Archived";
}

export interface BdBonusPlan {
  _id: string;
  planName: string;
  businessDeveloper: { _id: string; fullName: string; email: string; role?: string } | string | null;
  tiers: BdBonusTier[];
  status: "Active" | "Archived";
  notes: string;
}

export interface BdAssignment {
  _id: string;
  businessDeveloper: { _id: string; fullName: string; email: string; role?: string } | string;
  target: BdTarget;
  bonusPlan: BdBonusPlan | null;
  assignmentDate: string;
  assignedBy: { _id: string; fullName: string; email: string; role?: string } | string;
  status: "Active" | "Archived";
  revenueAchieved?: number;
  remainingRevenue?: number;
  achievementPercentage?: number;
  statusLabel?: string;
  bonusStatus?: string;
}

export interface BdWorkspaceTargetData {
  assignment: BdAssignment | null;
  metrics: {
    currentRevenue: number;
    targetRevenue: number;
    remainingRevenue: number;
    achievementPercentage: number;
    periodStart?: string;
    periodEnd?: string;
  } | null;
}

export type AssignmentSaveResult = {
  ok: boolean;
  conflict?: BdAssignment;
  message?: string;
};

export interface BdEmployee {
  _id: string;
  fullName: string;
  email: string;
  role?: string;
}

interface Store {
  loading: boolean;
  allTargetsLoading: boolean;
  overview: {
    targets: BdTarget[];
    plans: BdBonusPlan[];
    assignments: BdAssignment[];
  } | null;
  developers: BdEmployee[];
  fetchDevelopers: () => Promise<void>;
  monthlyAchievement: any | null;
  fetchMonthlyAchievement: () => Promise<void>;
  allTargets: BdTarget[];
  workspace: BdWorkspaceTargetData | null;
  historicalRecords: BdTarget[];
  activityLogs: Array<{ _id: string; action: string; details: string; timestamp: string; user: any }>;
  fetchOverview: () => Promise<void>;
  fetchAllTargets: () => Promise<void>;
  fetchWorkspace: () => Promise<void>;
  fetchHistorical: () => Promise<void>;
  fetchActivityLogs: () => Promise<void>;
  createTarget: (body: Partial<BdTarget>) => Promise<boolean>;
  updateTarget: (id: string, body: Partial<BdTarget>) => Promise<boolean>;
  archiveTarget: (id: string) => Promise<boolean>;
  createPlan: (body: Partial<BdBonusPlan>) => Promise<boolean>;
  updatePlan: (id: string, body: Partial<BdBonusPlan>) => Promise<boolean>;
  createAssignment: (body: { businessDeveloper: string; targetId: string; bonusPlanId?: string }) => Promise<AssignmentSaveResult>;
  updateAssignment: (id: string, body: { businessDeveloper: string; targetId: string; bonusPlanId?: string }) => Promise<AssignmentSaveResult>;
}

const extractConflict = (err: any) => {
  const data = err?.response?.data;
  if (err?.response?.status === 409 && data?.existingAssignment) {
    return {
      conflict: data.existingAssignment as BdAssignment,
      message: data?.message || "An active assignment already exists.",
    };
  }
  return null;
};

export const useBdTargetBonusStore = create<Store>((set, get) => ({
  loading: false,
  allTargetsLoading: false,
  overview: null,
  developers: [],
  allTargets: [],
  workspace: null,
  historicalRecords: [],
  activityLogs: [],

  fetchDevelopers: async () => {
    try {
      const { data } = await axiosInstance.get("/bd-target-bonus/employees");
      set({ developers: data.developers || [] });
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to load BD employees");
    }
  },

  monthlyAchievement: null,
  fetchMonthlyAchievement: async () => {
    try {
      const { data } = await axiosInstance.get("/bd-target-bonus/monthly-achievement");
      set({ monthlyAchievement: data });
    } catch {
      // non-critical
    }
  },

  fetchOverview: async () => {
    set({ loading: true });
    try {
      const { data } = await axiosInstance.get("/bd-target-bonus/overview");
      set({ overview: data });
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to load target overview");
    } finally {
      set({ loading: false });
    }
  },

  fetchWorkspace: async () => {
    try {
      const { data } = await axiosInstance.get("/bd-target-bonus/workspace");
      set({ workspace: data });
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to load workspace data");
    }
  },

  fetchHistorical: async () => {
    try {
      const { data } = await axiosInstance.get("/bd-target-bonus/historical-records");
      set({ historicalRecords: data.records || [] });
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to load historical records");
    }
  },

  fetchActivityLogs: async () => {
    try {
      const { data } = await axiosInstance.get("/bd-target-bonus/activity-logs");
      set({ activityLogs: data.logs || [] });
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to load activity logs");
    }
  },

  fetchAllTargets: async () => {
    set({ allTargetsLoading: true });
    try {
      const { data } = await axiosInstance.get("/bd-target-bonus/targets");
      set({ allTargets: data.targets || [] });
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to load targets");
    } finally {
      set({ allTargetsLoading: false });
    }
  },

  createTarget: async (body) => {
    try {
      await axiosInstance.post("/bd-target-bonus/targets", body);
      toast.success("Target created");
      await Promise.all([get().fetchOverview(), get().fetchAllTargets()]);
      return true;
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to create target");
      return false;
    }
  },

  updateTarget: async (id, body) => {
    try {
      await axiosInstance.put(`/bd-target-bonus/targets/${id}`, body);
      toast.success("Target updated");
      await Promise.all([get().fetchOverview(), get().fetchAllTargets()]);
      return true;
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to update target");
      return false;
    }
  },

  archiveTarget: async (id) => {
    try {
      await axiosInstance.patch(`/bd-target-bonus/targets/${id}/archive`);
      toast.success("Target archived");
      await Promise.all([get().fetchOverview(), get().fetchAllTargets()]);
      return true;
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to archive target");
      return false;
    }
  },

  createPlan: async (body) => {
    try {
      await axiosInstance.post("/bd-target-bonus/bonus-plans", body);
      toast.success("Bonus plan created");
      await get().fetchOverview();
      return true;
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to create bonus plan");
      return false;
    }
  },

  updatePlan: async (id, body) => {
    try {
      await axiosInstance.put(`/bd-target-bonus/bonus-plans/${id}`, body);
      toast.success("Bonus plan updated");
      await get().fetchOverview();
      return true;
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to update bonus plan");
      return false;
    }
  },

  createAssignment: async (body) => {
    try {
      await axiosInstance.post("/bd-target-bonus/assignments", body);
      toast.success("Assignment created");
      await get().fetchOverview();
      return { ok: true };
    } catch (err: any) {
      const conflict = extractConflict(err);
      if (conflict) {
        return { ok: false, ...conflict };
      }
      toast.error(err?.response?.data?.message || "Failed to create assignment");
      return { ok: false, message: err?.response?.data?.message || "Failed to create assignment" };
    }
  },

  updateAssignment: async (id, body) => {
    try {
      await axiosInstance.put(`/bd-target-bonus/assignments/${id}`, body);
      toast.success("Assignment updated");
      await get().fetchOverview();
      return { ok: true };
    } catch (err: any) {
      const conflict = extractConflict(err);
      if (conflict) {
        return { ok: false, ...conflict };
      }
      toast.error(err?.response?.data?.message || "Failed to update assignment");
      return { ok: false, message: err?.response?.data?.message || "Failed to update assignment" };
    }
  },
}));

